import React, { useEffect, useRef } from 'react';

import { Box } from '@atlaskit/primitives/compiled/box';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import {
	getContentMacroMeasurementScope,
	observeContentMacroDimensions,
} from '@confluence/browser-metrics/entry-points/ContentMacroMeasurements.ts';
import { usePageState } from '@confluence/page-context';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

type Props = React.PropsWithChildren<{
	extensionKey: string;
	nodeType: string;
	isEditing?: boolean;
}>;

function MeasuredForgeMacro({ extensionKey, children }: Props) {
	const elementRef = useRef<HTMLDivElement>(null);
	const [{ contentId }] = usePageState();
	const isSSROrHydrating = useIsSSROrHydrating();
	const { createEvent } = useConfluenceAnalyticsEvents();
	useEffect(() => {
		const element = elementRef.current;
		if (!element || isSSROrHydrating) {
			return;
		}
		return observeContentMacroDimensions(element, 'forge', extensionKey, ({ height, width }) => {
			createEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'forgeMacroRenderer',
					action: 'render',
					actionSubject: 'forgeMacro',
					attributes: { extensionKey, height, width },
				},
			}).fire();
		});
	}, [contentId, extensionKey, isSSROrHydrating, createEvent]);
	return <Box ref={elementRef}>{children}</Box>;
}

export function shouldMeasureForgeMacros(isEditing?: boolean): boolean {
	return !isEditing && getContentMacroMeasurementScope().forge;
}

/** Match Connect: block and bodied extensions in renderer-hosted views, including live pages. */
export function ForgeMacroMeasurements(props: Props): React.JSX.Element {
	if (
		(props.nodeType !== 'extension' && props.nodeType !== 'bodiedExtension') ||
		!props.extensionKey ||
		!shouldMeasureForgeMacros(props.isEditing)
	) {
		return <>{props.children}</>;
	}
	return <MeasuredForgeMacro {...props} />;
}
